import { GrowthBookProvider as GrowthBProvider, GrowthBook } from '@growthbook/growthbook-react';
import React, { ReactNode, useEffect } from 'react';
import store from 'store2';

import { captureException } from '@skippay/core/global/errorTracking';

const FEATURES_CACHE_KEY = 'growthbook_features';
const FEATURES_CACHE_MAX_AGE = 48 * 60 * 60 * 1000; // 2 days

const growthbook = new GrowthBook({
	apiHost: 'https://cdn.growthbook.io',
	clientKey: process.env.GROWTH_BOOK,
	enableDevMode: process.env.NODE_ENV === 'development' || process.env.TARGET_ENV === 'accept',
	attributes: {
		environment: process.env.TARGET_ENV === 'prod' ? 'production' : 'accept',
	},
	trackingCallback: (experiment, result) => {
		if ('dataLayer' in window) {
			window.dataLayer.push({
				event: 'experiment_viewed',
				content_name: result.name,
				content_id: `${experiment.key}:${result.variationId}`,
			});
		}
	},
});

function checkUntilGaDefined(callback: (value: string) => void, tryCount = 0): void {
	// kill repeat calling after 2.5 seconds to avoid infinite loop
	if (tryCount === 10) {
		return;
	}

	const gaValue = document.cookie.match(/_ga=(.+?);/)?.[1];

	if (typeof gaValue !== 'undefined') {
		callback(gaValue);
	} else {
		setTimeout(() => checkUntilGaDefined(callback, tryCount ? tryCount + 1 : 1), 250);
	}
}

function loadCachedFeatures() {
	const cachedFeatures = store.local.get(FEATURES_CACHE_KEY);

	if (cachedFeatures) {
		try {
			const { features, timestamp } = cachedFeatures;
			const now = Date.now();
			if (features && timestamp && now - timestamp < FEATURES_CACHE_MAX_AGE) {
				growthbook.setFeatures(features);
			} else {
				store.local.remove(FEATURES_CACHE_KEY);
			}
		} catch {
			// ignore cache errors (e.g. invalid JSON)
		}
	}
}

function setup(userId: string) {
	growthbook
		.loadFeatures({
			timeout: 2000,
		})
		.then(() => {
			store.local.set(FEATURES_CACHE_KEY, {
				features: growthbook.getFeatures(),
				timestamp: Date.now(),
			});
		})
		.catch((originalError) =>
			captureException(new Error('Failed to load AB test features'), {
				extra: { originalError },
			})
		);
	growthbook.setAttributes({
		id: userId,
		viewportWidth: window.innerWidth,
	});
}

function GrowthBookProvider({ children }: { children?: ReactNode | undefined }): JSX.Element {
	useEffect(() => {
		loadCachedFeatures();

		if (process.env.NODE_ENV === 'development') {
			// Use a static user ID in development so we can see the same variation
			setup('00001');
		} else {
			// Load features asynchronously when the app renders
			checkUntilGaDefined((value) => {
				const gaUserId = value.split('.').slice(-2).join('.');

				if (!gaUserId) {
					return;
				}

				setup(gaUserId);
			});
		}
	}, []);

	return <GrowthBProvider growthbook={growthbook}>{children}</GrowthBProvider>;
}

export default GrowthBookProvider;
